// Shared vocabulary for the AD policy screens.
//
// Everything the evaluator actually understands is declared here once, so the
// create form, the list and the discover drawer can't drift apart on what a
// policy is allowed to say.

// ─── Actions ────────────────────────────────────────────────────────────────
// The engine accepts exactly three policyFlow values. "deny" is what older
// policies stored and what the UI still calls the block action; "notify" was
// retired — it never did anything the engine acted on, so those policies read
// as Allow.
export const ACTIONS = [
  {
    value: "allow",
    label: "Allow",
    tone: "green",
    hint: "Let the request through.",
  },
  {
    value: "mfa_required",
    // Short form for pills, filters and the discover drawer; the create form
    // uses the long, shared wording ("Require MFA — push to the user's phone")
    // from components/policyShell, since approval by someone else is a
    // separate action.
    label: "Require MFA",
    tone: "red",
    hint: "Step-up before the request goes through.",
  },
  {
    value: "block",
    label: "Deny",
    tone: "red-dark",
    hint: "Block & log.",
  },
];

export const ACTION_VALUES = ACTIONS.map((a) => a.value);

export const RETIRED_ACTION_NOTICE =
  "The Notify action was retired — the engine never acted on it. This policy now evaluates as Allow.";

// Maps anything that has ever been written into policyFlow onto the three
// values the engine accepts. Unknown values fall back to allow, which is what
// the evaluator does with a flow it doesn't recognise.
export function normalizeAction(flow) {
  const v = String(flow || "").toLowerCase();
  if (v === "mfa_required" || v === "mfa") return "mfa_required";
  if (v === "block" || v === "deny") return "block";
  return "allow";
}

export function isRetiredAction(flow) {
  return String(flow || "").toLowerCase() === "notify";
}

export function actionLabel(flow) {
  const v = normalizeAction(flow);
  return (ACTIONS.find((a) => a.value === v) || ACTIONS[0]).label;
}

// Reads the effective action off a stored policy, whatever shape it's in.
export function policyAction(policyJson) {
  const j = policyJson || {};
  const raw =
    j.policyKind === "risk_based"
      ? j.aboveThresholdFlow || j.policyFlow
      : j.policyFlow;
  return normalizeAction(raw);
}

// ─── Protocols ──────────────────────────────────────────────────────────────
// The server 400s on anything outside these four.
export const PROTOCOLS = [
  { value: "kerberos", label: "Kerberos" },
  { value: "ntlm", label: "NTLM" },
  { value: "ldap", label: "LDAP" },
  { value: "smb", label: "SMB" },
];

export const PROTOCOL_VALUES = PROTOCOLS.map((p) => p.value);

export function sanitizeProtocols(list) {
  const seen = new Set();
  return (Array.isArray(list) ? list : [])
    .map((p) => String(p || "").toLowerCase())
    .filter((p) => PROTOCOL_VALUES.includes(p) && !seen.has(p) && seen.add(p));
}

// ─── Source / destination predicates ────────────────────────────────────────

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

export function isValidSource(token) {
  const t = String(token || "").trim();
  if (!t) return false;
  const [addr, mask, ...rest] = t.split("/");
  if (rest.length > 0) return false;
  if (addr.includes(":")) {
    // IPv6 — accept the shape, leave the exhaustive check to the engine.
    if (!/^[0-9a-fA-F:]+$/.test(addr)) return false;
    if (mask === undefined) return true;
    const bits = Number(mask);
    return /^\d{1,3}$/.test(mask) && bits >= 0 && bits <= 128;
  }
  if (!IPV4.test(addr)) return false;
  if (mask === undefined) return true;
  const bits = Number(mask);
  return /^\d{1,2}$/.test(mask) && bits >= 0 && bits <= 32;
}

export const SOURCE_HINT = "An exact IP (10.0.4.21) or a CIDR range (10.0.4.0/24).";

// Four accepted forms:
//   *                     every destination
//   cifs/*                every host, one service class
//   cifs/fs01.corp.lab    one service on one host
//   fs01.corp.lab         one host, any service class
export function isValidDestination(token) {
  const t = String(token || "").trim();
  if (!t) return false;
  if (t === "*") return true;
  const host = /^[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?)*$/;
  if (t.includes("/")) {
    const [svc, rest, ...extra] = t.split("/");
    if (extra.length > 0) return false;
    if (!/^[A-Za-z0-9_-]+$/.test(svc)) return false;
    return rest === "*" || host.test(rest);
  }
  return host.test(t);
}

export const DESTINATION_HINT =
  "*, cifs/*, cifs/fs01.corp.lab or fs01.corp.lab (host, any service class).";

// Trims, drops blanks and de-duplicates — what actually goes on the wire.
export function cleanTokens(list) {
  const seen = new Set();
  return (Array.isArray(list) ? list : [])
    .map((t) => String(t || "").trim())
    .filter((t) => t && !seen.has(t) && seen.add(t));
}

export function invalidTokens(list, validator) {
  return cleanTokens(list).filter((t) => !validator(t));
}

// ─── MFA prompt template ────────────────────────────────────────────────────
// Substituted by the push sender. Anything else that looks like a variable is
// forwarded to the phone literally, so unknown $words are worth flagging.
export const MFA_VARIABLES = [
  { token: "$username", desc: "sAMAccountName", sample: "jdoe" },
  { token: "$user", desc: "display name", sample: "Jane Doe" },
  { token: "$domain", desc: "AD domain", sample: "corp.lab" },
  { token: "$source", desc: "source address", sample: "10.0.4.21" },
  { token: "$destination", desc: "destination", sample: "cifs/fs01.corp.lab" },
  { token: "$protocol", desc: "auth protocol", sample: "kerberos" },
];

export const MFA_VARIABLE_TOKENS = MFA_VARIABLES.map((v) => v.token);

export const DEFAULT_MFA_PROMPT =
  "$user is signing in to $destination via $protocol from $source";

// Longest-token-first so $username is never matched as $user + "name".
const VARIABLE_PATTERN = /\$[A-Za-z_][A-Za-z0-9_]*/g;

export function unknownVariables(template) {
  const found = String(template || "").match(VARIABLE_PATTERN) || [];
  const seen = new Set();
  return found.filter(
    (t) => !MFA_VARIABLE_TOKENS.includes(t) && !seen.has(t) && seen.add(t),
  );
}

// Renders the template the way the push sender would, using sample values for
// anything we can't source from the form.
export function renderMfaPrompt(template, values = {}) {
  const sample = {};
  MFA_VARIABLES.forEach((v) => {
    sample[v.token] = values[v.token] || v.sample;
  });
  return String(template || "").replace(VARIABLE_PATTERN, (match) =>
    MFA_VARIABLE_TOKENS.includes(match) ? sample[match] : match,
  );
}

// ─── Domains ────────────────────────────────────────────────────────────────
// getAllDomains rows carry two different names:
//   directoryName — the admin's label, e.g. "test01"
//   domainName    — the FQDN the sensor reports, e.g. "corp.lab"
// A policy matched against the wrong one matches nothing, so the label never
// goes on the wire and the FQDN is always read from domainName first.
// (`domain` / `dc1`+`dc2` are the older AD-service shape, kept as fallbacks.)
export function domainFqdn(domain) {
  if (!domain) return "";
  if (domain.domainName) return domain.domainName;
  if (domain.domain) return domain.domain;
  if (domain.dc1 && domain.dc2) return `${domain.dc1}.${domain.dc2}`;
  return "";
}

export function domainLabel(domain) {
  if (!domain) return "";
  return domain.directoryName || domainFqdn(domain) || "";
}

// enforcementMode is the domain's posture: "monitor" logs only, "enforce"
// acts on policy. Anything else is unknown rather than silently "enforce".
export function domainPosture(domain) {
  const m = String(domain?.enforcementMode || "").toLowerCase();
  return m === "monitor" || m === "enforce" ? m : "";
}

export function domainFallback(domain) {
  const f = String(domain?.fallbackAction || domain?.fallbackMode || "").toLowerCase();
  return f === "allow" || f === "deny" ? f : "allow";
}
